import {
  DeviceCheckNotes,
  DossierTable,
  FieldGroup,
  FieldProse,
  NumberedSection,
  obj,
  PanelCard,
  Placeholder,
  withInlinePlaceholders,
} from './DossierPrimitives';
import { consultationRows } from '../consultationRows';

/**
 * EU MDR Annex II Technical Documentation renderer — §1–§6 in Annex II order.
 *
 * Schema detected by the parent renderer via presence of
 * `s1_device_description` in the dossier dict (unique to AnnexIITechnicalDoc;
 * no US eSTAR/eCTD schema uses this key). submission_type for EU runs is one
 * of "IIa", "IIb", "IIb_implantable", "III".
 *
 * Key features:
 *   - Cover header block (device name, class, Basic UDI-DI, company, EC REP, NB)
 *   - §1  Device Description & Specification (all 19 sub-fields)
 *   - §2  Information Supplied by Manufacturer
 *   - §3  Design & Manufacturing
 *   - §4  GSPR as a scrollable table; grounded rows show "Source: <tool>" line
 *   - §5  Benefit-Risk & Risk Management
 *   - §6.1 Pre-clinical & Clinical Evidence
 *   - §6.2 Conditional Cases — rendered only when non-null
 *
 * Placeholder discipline: values equal to `__PLACEHOLDER__` or starting with
 * `__PLACEHOLDER__:` are surfaced by the shared Placeholder primitive as
 * amber italic "to complete" chips (identical to eSTAR/eCTD renderers).
 */

// ── Types ────────────────────────────────────────────────────────────────────

interface GsprEntry {
  gspr_ref?: unknown;
  applies?: unknown;
  justification_if_not_applicable?: unknown;
  conformity_method?: unknown;
  standards_or_cs_applied?: unknown[];
  controlled_document_identity?: unknown;
  evidence?: unknown;
  eu_grounded?: unknown;
  evidence_tool?: unknown;
}

interface SubmissionHeader {
  product_name?: string | null;
  company?: string | null;
  ec_rep?: string | null;
  notified_body?: string | null;
  prrc?: string | null;
  srn?: string | null;
}

// ── Main component ───────────────────────────────────────────────────────────

export function EuTdRenderer({
  dossier,
  submissionHeader,
  hideCover = false,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
  /** Grouped EU view: the shared header card supplies the identity, so the
   *  per-annex cover block is suppressed. Default false → standalone output is
   *  byte-identical. */
  hideCover?: boolean;
}) {
  return (
    <div className="space-y-4">
      {!hideCover && <EuCoverBlock dossier={dossier} submissionHeader={submissionHeader} />}
      <DeviceCheckNotes dossier={dossier} />
      <Section1 dossier={dossier} />
      <Section2 dossier={dossier} />
      <Section3 dossier={dossier} />
      <Section4 dossier={dossier} />
      <Section5 dossier={dossier} />
      <Section6 dossier={dossier} />
    </div>
  );
}

// ── Cover block ──────────────────────────────────────────────────────────────

function EuCoverBlock({
  dossier,
  submissionHeader,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
}) {
  const s1 = obj(dossier, 's1_device_description');
  const deviceClass = dossier['device_class'];
  const basicUdi = dossier['basic_udi_di'] ?? s1['b_basic_udi_di'];
  const classificationRule = dossier['classification_rule'];

  const company = submissionHeader?.company;
  const ecRep = submissionHeader?.ec_rep;
  const notifiedBody = submissionHeader?.notified_body;

  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU MDR Annex II Technical Documentation
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) 2017/745 — Annex II
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CoverCell
          label="Device trade name"
          value={submissionHeader?.product_name}
          fallbackHint="Device trade name"
        />
        <CoverCell label="Device class (EU MDR)" value={deviceClass} />
        <CoverCell
          label="Basic UDI-DI"
          value={basicUdi}
          fallbackHint="Assigned upon registration in EUDAMED"
        />
        {classificationRule != null && (
          <CoverCell label="Classification rule" value={classificationRule} />
        )}
        <CoverCell
          label="Manufacturer / legal manufacturer"
          value={company}
          fallbackHint="Manufacturer name and address"
        />
        <CoverCell
          label="EU Authorised Representative (EC REP)"
          value={ecRep}
          fallbackHint="EC REP name and address"
        />
        <CoverCell
          label="Notified Body (NB)"
          value={notifiedBody}
          fallbackHint="NB name and number (if applicable)"
        />
      </div>
    </PanelCard>
  );
}

function CoverCell({
  label,
  value,
  fallbackHint,
}: {
  label: string;
  value: unknown;
  fallbackHint?: string;
}) {
  const v = value == null ? '' : String(value).trim();
  const isPlaceholder = v === '__PLACEHOLDER__' || v.startsWith('__PLACEHOLDER__:');
  const hint = isPlaceholder
    ? v.startsWith('__PLACEHOLDER__:')
      ? v.slice('__PLACEHOLDER__:'.length).trim() || fallbackHint || label
      : fallbackHint || label
    : null;

  return (
    <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-navy">
        {isPlaceholder ? (
          <Placeholder hint={hint} />
        ) : v ? (
          v
        ) : (
          <Placeholder hint={fallbackHint || label} />
        )}
      </div>
    </div>
  );
}

// ── Section helpers ───────────────────────────────────────────────────────────

// §1 Device Description and Specification
const S1_FIELDS: [string, string][] = [
  ['a_name_and_general_description', 'Name and general description (§1(a))'],
  ['a_intended_purpose', 'Intended purpose (§1(a))'],
  ['a_intended_users', 'Intended users (§1(a))'],
  ['b_basic_udi_di', 'Basic UDI-DI (§1(b))'],
  ['c_intended_patient_population', 'Intended patient population (§1(c))'],
  ['c_medical_conditions', 'Medical conditions addressed (§1(c))'],
  ['d_principles_of_operation', 'Principles of operation (§1(d))'],
  ['d_mode_of_action', 'Mode of action (§1(d))'],
  ['e_rationale_qualification_as_medical_device', 'Rationale for qualification as medical device (§1(e))'],
  ['f_risk_class', 'Risk class (§1(f))'],
  ['f_classification_rule', 'Classification rule (§1(f))'],
  ['g_novel_features', 'Novel features (§1(g))'],
  ['h_accessories_and_combination_devices', 'Accessories and combination devices (§1(h))'],
  ['i_variants_and_configurations', 'Variants and configurations (§1(i))'],
  ['j_key_functional_elements', 'Key functional elements (§1(j))'],
  ['j_labelled_pictures', 'Labelled pictures (§1(j))'],
  ['j_raw_materials', 'Raw materials and components (§1(j))'],
  ['previous_generations', 'Previous generations of the device'],
  ['similar_devices_eu_intl_market', 'Similar devices on EU and international markets'],
];

function Section1({ dossier }: { dossier: Record<string, unknown> }) {
  const s1 = obj(dossier, 's1_device_description');
  const consultations = consultationRows(dossier['consultation_procedures']);
  return (
    <NumberedSection prefix="Section" num={1} title="Device Description and Specification">
      {S1_FIELDS.map(([key, label]) => {
        const val = s1[key];
        if (val == null) return null;
        return <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />;
      })}
      {consultations.length > 0 && (
        <FieldGroup label="Consultation procedures (Annex IX Section 5):">
          <DossierTable headers={['Procedure', 'Status', 'Basis']} rows={consultations} />
        </FieldGroup>
      )}
    </NumberedSection>
  );
}

// §2 Information Supplied by Manufacturer
const S2_FIELDS: [string, string][] = [
  ['labels_and_packaging', 'Labels and packaging'],
  ['instructions_for_use', 'Instructions for use (IFU)'],
  ['languages', 'Languages in which information is supplied'],
];

function Section2({ dossier }: { dossier: Record<string, unknown> }) {
  const s2 = obj(dossier, 's2_information_supplied');
  const hasContent = S2_FIELDS.some(([k]) => s2[k] != null);
  return (
    <NumberedSection prefix="Section" num={2} title="Information Supplied by the Manufacturer">
      {hasContent ? (
        S2_FIELDS.map(([key, label]) => {
          const val = s2[key];
          if (val == null) return null;
          return (
            <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
          );
        })
      ) : (
        <FieldProse
          label="Information supplied:"
          body={null}
          fallbackHint="Information supplied by manufacturer to be completed"
        />
      )}
    </NumberedSection>
  );
}

// §3 Design and Manufacturing
const S3_FIELDS: [string, string][] = [
  ['a_design_stages', 'Design stages and design outputs (§3(a))'],
  ['b_manufacturing_processes_validation', 'Manufacturing processes and validation (§3(b))'],
  ['c_all_sites', 'All manufacturing sites (§3(c))'],
];

function Section3({ dossier }: { dossier: Record<string, unknown> }) {
  const s3 = obj(dossier, 's3_design_and_manufacturing');
  const hasContent = S3_FIELDS.some(([k]) => s3[k] != null);
  return (
    <NumberedSection prefix="Section" num={3} title="Design and Manufacturing Information">
      {hasContent ? (
        S3_FIELDS.map(([key, label]) => {
          const val = s3[key];
          if (val == null) return null;
          return (
            <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
          );
        })
      ) : (
        <FieldProse
          label="Design and manufacturing:"
          body={null}
          fallbackHint="Design and manufacturing information to be completed"
        />
      )}
    </NumberedSection>
  );
}

// §4 GSPR table
function Section4({ dossier }: { dossier: Record<string, unknown> }) {
  const gspr = Array.isArray(dossier['s4_gspr'])
    ? (dossier['s4_gspr'] as GsprEntry[])
    : [];

  const headers = [
    'GSPR Ref.',
    'Applies?',
    'Conformity Method',
    'Standards / CS Applied',
    'Controlled Document',
    'Justification (if N/A)',
    'Evidence',
  ];

  return (
    <NumberedSection
      prefix="Section"
      num={4}
      title="General Safety and Performance Requirements (GSPR)"
    >
      <FieldGroup label="GSPR checklist:">
        {gspr.length === 0 ? (
          <DossierTable headers={headers} rows={[]} placeholderHint="GSPR table to be completed by submitter" />
        ) : (
          <div className="my-2">
            <table
              className="w-full border-collapse text-[12.5px]"
              style={{ tableLayout: 'fixed' }}
            >
              <colgroup>
                <col style={{ width: '8%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '17%' }} />
                <col style={{ width: '18%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead>
                <tr>
                  {headers.map((h) => (
                    <th
                      key={h}
                      className="text-left p-2 bg-[#f3f4f6] text-muted uppercase tracking-[0.04em] text-[11px] border-b border-line"
                      style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gspr.map((entry, ri) => (
                  <GsprRow key={ri} entry={entry} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </FieldGroup>
    </NumberedSection>
  );
}

function GsprRow({ entry }: { entry: GsprEntry }) {
  const gspr_ref = entry.gspr_ref != null ? String(entry.gspr_ref) : '';
  const applies = entry.applies ? 'Yes' : 'No';
  const conformity =
    entry.conformity_method != null ? String(entry.conformity_method) : null;
  const standards = Array.isArray(entry.standards_or_cs_applied)
    ? entry.standards_or_cs_applied.filter(Boolean).map(String).join(', ')
    : null;
  const controlledDoc =
    entry.controlled_document_identity != null
      ? String(entry.controlled_document_identity)
      : null;
  const justification =
    entry.justification_if_not_applicable != null
      ? String(entry.justification_if_not_applicable)
      : null;
  const evidence = entry.evidence != null ? String(entry.evidence) : null;
  const euGrounded = Boolean(entry.eu_grounded);
  const evidenceTool =
    entry.evidence_tool != null ? String(entry.evidence_tool) : null;

  const cells: [string | null, string][] = [
    [gspr_ref || null, 'GSPR reference'],
    [applies, ''],
    [conformity, 'Conformity method'],
    [standards, 'Standards'],
    [controlledDoc, 'Document ID'],
    [justification, 'Justification'],
    [evidence, 'Evidence'],
  ];

  return (
    <tr>
      {cells.map(([cellValue, hint], ci) => {
        // Special handling for evidence cell — may have grounding attribution
        if (ci === 6) {
          return (
            <td
              key={ci}
              className="p-2 border-b border-line align-top"
              style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
            >
              {cellValue != null ? (
                <>
                  {withInlinePlaceholders(cellValue)}
                  {euGrounded && evidenceTool && (
                    <div className="text-[11.5px] italic text-muted mt-1">
                      Source: {evidenceTool}
                    </div>
                  )}
                </>
              ) : (
                <span className="text-muted text-[12px]">—</span>
              )}
            </td>
          );
        }
        return (
          <td
            key={ci}
            className="p-2 border-b border-line align-top"
            style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
          >
            {cellValue != null ? (
              withInlinePlaceholders(cellValue)
            ) : hint ? (
              <Placeholder hint={hint} />
            ) : (
              <span className="text-muted text-[12px]">—</span>
            )}
          </td>
        );
      })}
    </tr>
  );
}

// §5 Benefit-Risk and Risk Management
const S5_FIELDS: [string, string][] = [
  ['a_benefit_risk_analysis', 'Benefit-risk analysis (§5(a))'],
  ['b_risk_management_rm_plan', 'Risk management plan (§5(b))'],
  ['b_risk_management_hazard_analysis_fmea', 'Hazard analysis / FMEA (§5(b))'],
  ['b_risk_management_rm_report', 'Risk management report (§5(b))'],
];

function Section5({ dossier }: { dossier: Record<string, unknown> }) {
  const s5 = obj(dossier, 's5_benefit_risk_and_risk_management');
  const hasContent = S5_FIELDS.some(([k]) => s5[k] != null);
  return (
    <NumberedSection
      prefix="Section"
      num={5}
      title="Benefit-Risk Analysis and Risk Management"
    >
      {hasContent ? (
        S5_FIELDS.map(([key, label]) => {
          const val = s5[key];
          if (val == null) return null;
          return (
            <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
          );
        })
      ) : (
        <FieldProse
          label="Benefit-risk and risk management:"
          body={null}
          fallbackHint="Benefit-risk analysis and risk management content to be completed"
        />
      )}
    </NumberedSection>
  );
}

// §6 Verification and Validation Evidence
const S61_FIELDS: [string, string][] = [
  ['engineering_lab_simulated_animal_tests', 'Engineering, lab, simulated-use and animal tests'],
  ['biocompatibility', 'Biocompatibility'],
  ['physical_chemical_microbiological', 'Physical, chemical and microbiological characterisation'],
  ['electrical_safety_emc', 'Electrical safety and EMC'],
  ['software_verification_validation', 'Software verification and validation'],
  ['stability_shelf_life', 'Stability and shelf life'],
  ['performance_and_safety', 'Performance and safety testing'],
  ['clinical_evaluation_report', 'Clinical evaluation report (CER)'],
  ['pmcf_plan_report', 'PMCF plan and report'],
];

const S62_FIELDS: [string, string][] = [
  ['ancillary_medicinal_substance', 'Ancillary medicinal substance'],
  ['tissues_cells_human_or_animal_origin', 'Tissues/cells of human or animal origin'],
  ['sterile_or_defined_microbiological_state', 'Sterile or defined microbiological state'],
  ['measuring_function', 'Measuring function'],
  ['connected_to_other_devices', 'Connected to or used with other devices'],
  ['cmr_or_endocrine_disruptor_over_0_1pct', 'CMR or endocrine-disrupting substances >0.1%'],
];

function Section6({ dossier }: { dossier: Record<string, unknown> }) {
  const s6 = obj(dossier, 's6_verification_and_validation');
  const s61 = obj(s6, 's6_1_preclinical_clinical');
  const s62Raw = s6['s6_2_conditional_cases'];
  const s62 =
    s62Raw && typeof s62Raw === 'object' && !Array.isArray(s62Raw)
      ? (s62Raw as Record<string, unknown>)
      : null;
  // §6.2 is rendered only if the object is present AND has at least one non-null field
  const s62HasContent =
    s62 != null && S62_FIELDS.some(([k]) => s62[k] != null);

  return (
    <NumberedSection prefix="Section" num={6} title="Verification and Validation Evidence">
      {/* §6.1 */}
      <h4 className="text-[14px] font-bold text-navy mt-2 mb-2">
        §6.1 — Pre-clinical and Clinical Evidence
      </h4>
      {S61_FIELDS.map(([key, label]) => {
        const val = s61[key];
        if (val == null) return null;
        return (
          <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
        );
      })}

      {/* §6.2 — conditional */}
      {s62HasContent && (
        <>
          <h4 className="text-[14px] font-bold text-navy mt-4 mb-2">
            §6.2 — Conditional Cases
          </h4>
          {S62_FIELDS.map(([key, label]) => {
            const val = s62![key];
            if (val == null) return null;
            return (
              <FieldProse key={key} label={`${label}:`} body={val} fallbackHint={label} />
            );
          })}
        </>
      )}
    </NumberedSection>
  );
}

// The §4 GSPR table is identical for MDR and IVDR (same `s4_gspr` shape + title),
// so the IVDR TD renderer reuses it rather than duplicating ~70 lines. One fix
// (e.g. the placeholder/table-fit work) then applies to both.
export { Section4 as EuGsprSection };
